/**
 * Meta description normaliser.
 *
 * Google truncates descriptions past ~160 characters and often ignores very
 * short ones. Every page on LifeWestWay routes its description through
 * `makeSeoDescription` so the final string always lands inside 130–155
 * characters, cut on a word boundary and never mid-word.
 */

const MIN_LENGTH = 130;
const MAX_LENGTH = 155;

/** Short, factual tails used to lift thin descriptions into the 150+ range. */
const TAILS = [
  "Updated for 2026 with official sources, real costs and clear timelines.",
  "2026 guidance with costs, eligibility rules and step-by-step next actions.",
  "Practical 2026 detail for expats, students, workers and remote professionals.",
  "Checked against official 2026 rules, fees, processing times and requirements.",
  "Clear 2026 breakdown of costs, paperwork and the steps that actually matter.",
];

function hash(value: string) {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function tidy(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function cutToWindow(value: string) {
  if (value.length <= MAX_LENGTH) return value;
  const window = value.slice(0, MAX_LENGTH + 1);
  const lastSpace = window.lastIndexOf(" ");
  const cut = (lastSpace > MIN_LENGTH - 20 ? window.slice(0, lastSpace) : value.slice(0, MAX_LENGTH))
    .replace(/[\s,;:—–-]+$/, "");
  if (cut.length >= MIN_LENGTH) return cut;
  // Word-boundary trimming left us just short of the window — close the
  // sentence rather than shipping a sub-150 description.
  const closed = `${cut} and more.`;
  return closed.length <= MAX_LENGTH ? closed : `${cut}…`;
}


/**
 * Returns a meta description guaranteed to be <= 160 characters and, whenever
 * there is enough source material, >= 150.
 *
 * @param input   The hand-written description for the page.
 * @param context Optional extra context (title, country, topic) used to pad
 *                descriptions that are too short on their own.
 */
export function makeSeoDescription(input: string, context = ""): string {
  let text = tidy(input ?? "");
  if (!text) text = tidy(context);
  if (!text) return TAILS[0];

  if (text.length > MAX_LENGTH) return cutToWindow(text);

  const extraContext = tidy(context);
  if (text.length < MIN_LENGTH && extraContext && !text.toLowerCase().includes(extraContext.toLowerCase())) {
    const merged = `${text.replace(/[.\s]+$/, "")}. ${extraContext.replace(/[.\s]+$/, "")}.`;
    if (merged.length <= MAX_LENGTH) text = merged;
    else if (merged.length > text.length && merged.length - MAX_LENGTH < 40) text = cutToWindow(merged);
  }

  let index = hash(text) % TAILS.length;
  let guard = 0;
  while (text.length < MIN_LENGTH && guard < TAILS.length) {
    const tail = TAILS[index];
    const merged = `${text.replace(/[.\s]+$/, "")}. ${tail}`;
    text = merged.length > MAX_LENGTH ? cutToWindow(merged) : merged;
    index = (index + 1) % TAILS.length;
    guard += 1;
  }

  return cutToWindow(text);
}
